import styled from '@emotion/styled';
import { useLocation } from 'react-router-dom';
import { forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens, Text, Button } from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { FloatingPanel } from '@hotjar/activation-toolkit';
import { getRoute, getRouteConfigByPath } from '@hotjar/webapp/utils/routing';
import { SURVEY_TEMPLATES, SURVEYS } from '@hotjar/webapp/constants/routes';
import { useCurrentSiteId } from '@hotjar/site';
import FeedbackBanner from './assets/feedback.jpg';

const Banner = styled.div`
  margin: 0 ${tokens.spacer.x1} ${tokens.spacer.x2} ${tokens.spacer.x1};
  height: 120px;
  background-position: center;
  background-size: cover;
  transition: background-image 500ms ease;
`;

const Content = styled.div`
  display: flex;
  margin: 0 ${tokens.spacer.x1};
  flex-direction: column;
  text-align: left;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const Title = styled.span`
  ${tokens.text('header5')};
  margin-bottom: ${tokens.spacer.x1};
`;

const UI_POSITION = 'feedback popup';
const COLLAPSED_KEY = 'feedbackCollapsed';

interface FeedbackPopup {
  onDismiss: () => void;
}

export const FeedbackPopup = forwardRef<HTMLElement, FeedbackPopup>(
  ({ onDismiss }, ref): JSX.Element | null => {
    const location = useLocation();
    const inSurveyCreatePage =
      SURVEYS.CREATE.name === (getRouteConfigByPath(location.pathname)?.name || '');
    const siteId = useCurrentSiteId();
    const { t } = useTranslation();
    const { click: trackClick, viewElement } = useTrack();
    const handleOnDismiss = () => {
      trackClick('skip', UI_POSITION, {});
      onDismiss();
    };
    const onToggle = (isCollapsed: boolean) => {
      trackClick(isCollapsed ? 'Collapse' : 'Expand', UI_POSITION, {});
    };

    const onCTAClick = () => {
      if (siteId) {
        trackClick('go to create survey', UI_POSITION, {});
        onDismiss();
      }
    };

    useEffect(() => {
      if (!inSurveyCreatePage) {
        viewElement('Feedback', 'FloatingSuggestions', {});
      }
    }, [viewElement, inSurveyCreatePage]);

    if (inSurveyCreatePage) {
      return null;
    }

    return (
      <FloatingPanel
        collapsedStateKey={COLLAPSED_KEY}
        remainingTasks={1}
        title={t('activation:feedback-popup.popup-title')}
        onDismiss={handleOnDismiss}
        onToggle={onToggle}
        ref={ref}
      >
        <Banner
          aria-hidden
          data-testid="feedback-section-banner"
          style={{ backgroundImage: `url('${FeedbackBanner}')` }}
        />

        <Content>
          <Title>{t('activation:feedback-popup.title')}</Title>
          <Text
            textColor="text_light"
            css={{ marginBottom: tokens.spacer.x2 }}
            as="p"
            textType="body2"
          >
            {t('activation:feedback-popup.description')}
          </Text>
          <ButtonWrapper>
            {siteId ? (
              <Button
                as="a"
                href={getRoute(SURVEY_TEMPLATES.BASE.path, { siteId })}
                onClick={onCTAClick}
                css={{ marginRight: tokens.spacer.x2 }}
                kind="primary"
              >
                {t('activation:feedback-popup.cta')}
              </Button>
            ) : null}
            <Button onClick={handleOnDismiss} kind="secondary">
              {t('activation:feedback-popup.skip')}
            </Button>
          </ButtonWrapper>
        </Content>
      </FloatingPanel>
    );
  },
);
